import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Order } from '../../../../../models/orderModel';
import { AdminService } from '../../../../../services/admin.service';
import moment from 'moment';
import { DatePipe } from '@angular/common';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { BackOffice24StatesService } from '../../state/back-office24-states.service';
import { MyConfigService } from '../../../../../services/my-config.service';

@Component({
  selector: 'app-back-office24-order-list',
  templateUrl: './back-office24-order-list.component.html',
  styleUrls: ['./back-office24-order-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24OrderListComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  orderListForm: UntypedFormGroup;
  orderId: string;
  orderNr: string;
  orders: Order[] = [];
  order: Order;
  dataSource: MatTableDataSource<Order> = new MatTableDataSource<Order>([]);
  dataSourceAS: MatTableDataSource<Order> = new MatTableDataSource<Order>([]);
  ordersAS: Order[] = [];
  isLoading = false;
  activeRequests: number = 0;
  chartLabels: string[] = ['Init'];
  chartData: number[] = [10];
  filtersExpanded: boolean = true;
  filtersASExpanded: boolean = true;


  displayedColumns: string[] = [
    'orderNr',
    'carId',
    'customerName',
    'cellular',
    'email',
    'comment',
    'orderDate',
    'status'
  ];
  displayedColumnsAS: string[] = [
    'orderNr',
    'customerName',
    'cellular',
    'email',
    'comment',
    'orderDate',
    'status',
    'serviceStatus'
  ];
  additionalServiceStatusValues = {
    "New": 1,
    "Paid": 2,
    "Planned": 4,
    "Underway": 8,
    "Completed": 16,
    "Removed": 32,
    "Cancelled": 64
  }
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('paginatorAS', { static: false }) paginatorAS: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  componentDestroyed$: Subject<boolean> = new Subject();
  tabChange$: Subject<void> = new Subject<void>();
  ordersLastUpdated: any;
  ordersASLastUpdated: any;

  constructor(
    public adminService: AdminService,
    public backofficeService: BackOfficeService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public backOffice24StatesService: BackOffice24StatesService,
    public myConfigService: MyConfigService
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    this.initOrderList();
    this.backOffice24StatesService.orders$.subscribe(
      (orders: any) => {
        if (orders && orders.data && orders.data.length > 0) {
          this.orders = orders.data;
          this.ordersLastUpdated = orders.lastUpdated;
          this.setOrderMatTableData();
          this.groupOrderDate();
        }
      }
    );

    // if(!this.orders || this.orders.length === 0){
    //   this.getOrders();
    // }

    this.backOffice24StatesService.ordersAS$.subscribe(
      (orders: any) => {
        this.ordersAS = orders?.data;
        this.ordersASLastUpdated = orders?.lastUpdated;
        if (this.ordersAS && this.ordersAS.length > 0) {
          this.setOrderMatTableDataAS();
        }
      }
    );
  }

  setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  initOrderList() {
    this.dateFrom = moment().add(-120, 'd').format();
    this.timeFrom = "00:00";
    this.dateTo = moment().add(1, 'd').format();
    this.timeTo = "00:00";
    this.initOrderListForm();
  }

  initOrderListForm() {
    this.orderListForm = this.fb.group({
      dateFrom: [this.dateFrom],
      timeFrom: [this.timeFrom],
      dateTo: [this.dateTo],
      timeTo: [this.timeTo],
      orderNr: [this.orderNr]
    });
  }

  getOrders() {
    this.incrementRequests();
    this.orders = [];
    this.setDateFormats();
    this.backofficeService
      .getOrdersBetweenDate(this.organization.organizationId, this.dateFrom, this.dateTo)
      .pipe(
        takeUntil(this.componentDestroyed$),
        takeUntil(this.tabChange$)
      )
      .subscribe({
        next: (orders: Order[]) => {
          this.orders = orders;
          this.backOffice24StatesService.setOrders(this.orders);
          this.orders.forEach(order => {
            if (order.customer) {
              order.customerName = order.customer.firstName + ' ' + order.customer.lastName;
              order.cellular = order.customer.cellular;
              order.email = order.customer.email;
            }
          });
          this.setOrderMatTableData();
        },
        error: (error) => {
          console.error('Error fetching orders:', error);
          this.decrementRequests();
        },
        complete: () => {
          this.decrementRequests();
        }
      })
  }

  setOrderMatTableData() {
    this.dataSource = new MatTableDataSource<Order>(this.orders);
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;

      if (this.dataSource.sort) {
        this.dataSource.sort.active = 'orderNr';
        this.dataSource.sort.direction = 'desc';
        this.dataSource.sort.sortChange.emit({
          active: 'orderNr',
          direction: 'desc'
        });
      }
    }
  }

  getOrdersWithAdditionalServices() {
    this.incrementRequests();
    this.setDateFormats();
    this.ordersAS = [];

    this.backofficeService.getOrdersWithAdditionalServicesBetweenDate(this.organization.organizationId, this.dateFrom, this.dateTo)
      .pipe(
        takeUntil(this.componentDestroyed$),
        takeUntil(this.tabChange$)
      )
      .subscribe({
        next: (orders: Order[]) => {
          orders.forEach(order => {
            if (order.customer) {
              // Add these properties to the Order object
              order.customerName = order.customer.firstName + ' ' + order.customer.lastName;
              order.cellular = order.customer.cellular;
              order.email = order.customer.email;
            }
          });
          this.ordersAS = orders.sort((a, b) => b.orderNr - a.orderNr);

          this.backOffice24StatesService.setOrdersAS(this.ordersAS);

          this.setOrderMatTableDataAS();
          this.decrementRequests();
        },
        error: (error) => {
          console.error('Error fetching orders with additional services:', error);
          this.decrementRequests();
        }
      });
  }

  setOrderMatTableDataAS() {
    this.dataSourceAS = new MatTableDataSource<Order>(this.ordersAS);
    if (this.dataSourceAS) {
      this.dataSourceAS.paginator = this.paginatorAS;
      this.dataSourceAS.sort = this.sort;

      if (this.dataSourceAS.sort) {
        this.dataSourceAS.sort.active = 'orderNr';
        this.dataSourceAS.sort.direction = 'desc';
        this.dataSourceAS.sort.sortChange.emit({
          active: 'orderNr',
          direction: 'desc'
        });
      }
    }
  }

  getOrderNr() {
    this.incrementRequests();
    this.orderNr = this.orderListForm.controls['orderNr'].value;

    this.backofficeService
      .getOrderNr(this.orderNr)
      .pipe(
        takeUntil(this.componentDestroyed$),
        takeUntil(this.tabChange$)
      )
      .subscribe({
        next: (order: Order) => {
          this.goToOrder(order);
        },
        error: (error) => {
          console.error('Error fetching order:', error);
          this.decrementRequests();
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  groupOrderDate() {

    var barChartLabels = [];
    var barChartData = [];

    var groupedOrders = this.orders.reduceRight((p, c) => {
      var orderDate = moment(c.orderDate).format('YYYY-MM-DD')
      if (!p.hasOwnProperty(orderDate)) {
        p[orderDate] = 0;
      }
      p[orderDate] = p[orderDate] + 1
      return p;
    }, {});

    for (const order in groupedOrders) {
      barChartLabels.push(order);
      barChartData.push(groupedOrders[order]);
    }

    this.chartLabels = barChartLabels;
    this.chartData = barChartData;
  }

  goToOrder(order: Order): void {
    this.order = order;
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${order.orderId}`]));
    window.open(url, '_blank');
  }

  setDateFormats() {
    this.dateFrom = this.datepipe.transform(this.orderListForm.controls['dateFrom'].value, 'yyyy-MM-dd') + ' ' + this.orderListForm.controls['timeFrom'].value;
    this.dateTo = this.datepipe.transform(this.orderListForm.controls['dateTo'].value, 'yyyy-MM-dd') + ' ' + this.orderListForm.controls['timeTo'].value;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.dataSource) {
      this.dataSource.filter = filterValue.trim().toLowerCase();

      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  applyFilterAS(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.dataSourceAS) {
      this.dataSourceAS.filter = filterValue.trim().toLowerCase();

      if (this.dataSourceAS.paginator) {
        this.dataSourceAS.paginator.firstPage();
      }
    }
  }

  tabChanged(event: MatTabChangeEvent) {
    // Cancel any pending requests
    this.tabChange$.next();
  }

  getLeastValuedStatus(additionalServices: any[]): string | undefined {
    let leastStatus: string | undefined = undefined;
    let minValue = Infinity;

    for (const additionalService of additionalServices) {
      const value = this.additionalServiceStatusValues[additionalService.statusName];
      if (value !== undefined && value < minValue) {
        minValue = value;
        leastStatus = additionalService.statusName;
      }
    }
    return leastStatus;
  }

  flatten(obj: any) {
    return Object.keys(obj).reduce((acc, current) => {
      const _key = `${current}`;
      const currentValue = obj[current];
      if (Array.isArray(currentValue) || Object(currentValue) === currentValue) {
        Object.assign(acc, this.flatten(currentValue));
      } else {
        acc[_key] = currentValue;
      }
      return acc;
    }, {});
  }
  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  toggleFiltersAS(): void {
    this.filtersASExpanded = !this.filtersASExpanded;
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
    this.tabChange$.complete();
  }
}
